import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Shared AppAuthProvider for all Airclou apps.
 *
 * Encapsulates the common auth lifecycle:
 *   1. Cookie-based session bootstrap (airclou_session → Bearer JWT)
 *   2. Impersonation token detection from URL
 *   3. Periodic token refresh scheduling
 *   4. User profile fetching via app-supplied callback
 *
 * Each app provides an `AppAuthConfig` with its own token storage, user
 * fetch, and logout behaviour.  The provider exposes a unified `useAppAuth()`
 * hook so downstream consumers (ProtectedRoute, WorkspaceProvider, etc.)
 * share the same API surface regardless of which app they live in.
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, } from 'react';
import { getServiceApiUrl, getCsrfToken } from '@airclou/utils';
import { IMPERSONATION_TOKEN_PARAM, getImpersonationInfo, } from '@airclou/auth'; // shared exports from the slimmed @airclou/auth package
import { IDENTITY_MISMATCH_EVENT, registerIdentityReconciler } from './identityMismatch';
import { decodeJwtPayload, isLiveImpersonation, exchangeCookieForToken, clearScopeKeys as clearScopeKeysFor, renewSessionToken, authRequestSignal, IDENTITY_ADOPTED_EVENT, } from './sessionToken';
import { clearSuiteAuthKeys, signOutOfSuite, onSuiteSignOut } from './suiteSession';
import { registerTokenRecovery } from './tokenRecovery';
// ---------------------------------------------------------------------------
// Token helpers (pure functions — no side-effects)
// ---------------------------------------------------------------------------
// Token refresh constants
const MAX_REFRESH_INTERVAL_SECONDS = 6 * 60 * 60; // 6 hours
/**
 * Floor between identity re-checks against the session cookie, so tabbing back
 * and forth doesn't hammer /auth/me. A mismatch reported by an API client
 * bypasses this floor — see IDENTITY_MISMATCH_EVENT.
 */
const IDENTITY_RECHECK_MIN_INTERVAL_MS = 30 * 1000;
/**
 * How close to expiry a token has to be for the mount-time top-up to run, and the
 * point at which scheduled renewal switches from "wait" to "halve what is left".
 *
 * One hour, not the 24 it was. The default token lifetime is now 24h, so a 24h
 * threshold made `tokenNeedsRefresh` true for *any* token older than a second —
 * every page load ran a full `/auth/refresh`, cross-host Dashboard round trip and
 * all, which is precisely the expensive path this design keeps off the hot path.
 * With 7-day tokens that branch had been false for six days, so nothing noticed.
 */
const TOKEN_REFRESH_THRESHOLD_SECONDS = 60 * 60; // 1 hour
/**
 * How often the session tick wakes up to decide whether to call.
 *
 * A dumb fixed cadence, deliberately: the previous computed delay needed five
 * interacting clamps and each review round found a new hole in the arithmetic. One
 * comparison a minute per tab costs nothing — the per-tab tracker this replaced ran
 * every second — and it is the decision, not the timer, that governs network load.
 */
const SESSION_TICK_INTERVAL_SECONDS = 60;
/**
 * Report the deadline is approaching once it is this close.
 *
 * Also the window the expiry warning is shown in, so a user who returns inside it
 * has both the toast and its button.
 */
const IDLE_REPORT_MARGIN_SECONDS = 5 * 60;
/**
 * How often to refresh presence while someone is actively working.
 *
 * Well inside the idle threshold, and well above the tick cadence: without it a tab
 * in continuous use would call every minute, which is the difference between a few
 * requests a second across the fleet and a few hundred.
 */
const ACTIVITY_REPORT_INTERVAL_SECONDS = 10 * 60;
/**
 * When a tab comes back into view, ask the server about its session if it has not
 * answered for this long.
 *
 * Revoking a session on the sessions screen deletes its row, but no app backend
 * reads that row, so the revoked tab keeps working until its next call to auth —
 * up to ten minutes for someone working in it, up to the token's lifetime for one
 * left idle. Someone walking up to that device is the case that matters, and they
 * arrive by bringing the tab forward, so that is when the tab checks. The floor
 * keeps quick tabbing back and forth to one call a minute.
 *
 * The check is a read — `/auth/session/me` with the tab's own token, skipped when
 * the cookie already named this tab's session — so the common answer
 * ("still there") writes nothing and swaps no token. Only a `SESSION_REVOKED`
 * answer escalates to a renewal, which either adopts a newer session the cookie
 * names or ends the tab.
 */
const FOCUS_SESSION_CHECK_MIN_INTERVAL_MS = 60 * 1000;
/**
 * How long a tab may go without re-deriving its claims from Dashboard.
 *
 * The cheap activity call copies claims forward, which is what keeps it cheap — but
 * it means role and workspace membership stop being rechecked. Before this change an
 * open tab went through `/auth/refresh` at least this often, so removing someone
 * from a workspace took effect within the window; without it, not until the tab was
 * reloaded, which for a long-lived dashboard could be days. Matched to the old
 * interval deliberately: the point is to hold the previous guarantee, not improve on
 * it.
 */
const CLAIM_REFRESH_INTERVAL_MS = MAX_REFRESH_INTERVAL_SECONDS * 1000;
/**
 * How long to leave the heavy path alone after it failed to renew.
 *
 * It answers some conditions deterministically and not with a 401 — a user removed
 * from the workspace their token names gets a 403 — and the client reads any
 * non-401 as "no answer", which is right for the credential and wrong as a retry
 * policy: with the claims still stale, the tick asked again every minute, two
 * cross-host Dashboard calls per tab, for as long as the tab stayed open.
 */
const CLAIM_REFRESH_RETRY_MS = 10 * 60 * 1000;
/**
 * How long to let the "signing out" explanation show before navigating.
 *
 * Long enough to read, short enough not to feel like a hang. The login page
 * repeats the reason in its URL, so this is the belt to that braces rather than
 * the only explanation.
 */
const SIGN_OUT_EXPLANATION_MS = 1200;
/**
 * Input events that count as a human being present.
 *
 * This lives in the provider rather than in `ActivityTracker` because every app
 * mounts the provider and only four mount a tracker — conductor, form-builder and
 * the launcher would otherwise have no way to report presence and would go idle
 * while someone was working in them.
 */
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
/** At most one activity stamp per second; these events fire in floods. */
const ACTIVITY_STAMP_THROTTLE_MS = 1000;
/**
 * How close to expiry counts as "renew now", for *this* token.
 *
 * Relative to the token's own lifetime, capped at the hour above. An absolute hour
 * is fine against a 24h token and wrong against the 30m one the rollout ends on: a
 * 30-minute token is *always* within an hour of expiry, so every tab would have
 * renewed on every tick and every page load would have run the heavy Dashboard
 * round trip — the load the plan's cadence table says this design avoids. Half
 * the lifetime means two renewals per lifetime whatever the lifetime is.
 */
function refreshThresholdSeconds(token) {
    const payload = decodeJwtPayload(token);
    const lifetime = payload?.exp && payload?.iat ? payload.exp - payload.iat : 0;
    return lifetime > 0 ? Math.min(TOKEN_REFRESH_THRESHOLD_SECONDS, lifetime / 2) : TOKEN_REFRESH_THRESHOLD_SECONDS;
}
function tokenNeedsRefresh(token) {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp)
        return false;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp - now < refreshThresholdSeconds(token);
}
/**
 * When the token's role and workspace claims were last derived from Dashboard.
 *
 * Login and `/auth/refresh` derive them fresh, so their `iat` is that moment;
 * `/session/activity` copies them forward and carries the original moment as
 * `claimsAt`. Read from the token rather than remembered in a ref: a ref seeded at
 * mount restarted the six-hour clock on every reload, so a tab reloaded every few
 * hours never re-derived and a revoked workspace membership never took effect.
 */
function claimsDerivedAtMs(token) {
    const payload = decodeJwtPayload(token);
    const at = typeof payload?.claimsAt === 'number' ? payload.claimsAt : payload?.iat;
    return typeof at === 'number' ? at * 1000 : null;
}
function getSecondsUntilExpiry(token) {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp)
        return 0;
    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, payload.exp - now);
}
const AppAuthContext = createContext(undefined);
// Set synchronously at the start of any sign-out whose *navigation this provider
// owns*, so the unauthenticated redirect in ProtectedRoute steps aside and lets
// `onLogout(reason)` build the URL. Without it, `setUser(null)` re-renders
// ProtectedRoute, which writes `window.location.href` during that render and wins
// the race — the user lands on a generic login page with no `reason=inactivity`,
// and the "signing out" explanation never paints.
//
// Both the manual path and the server-refused paths (idle, revoked) set it. An
// earlier version set it only for manual sign-out on the theory that the
// involuntary paths *wanted* ProtectedRoute's redirect (it carries `redirect=`);
// that is no longer true now those paths build their own URL with both the reason
// and the return address. The ordinary 401-expiry path still does not set it, and
// still relies on ProtectedRoute.
let signOutNavigationInProgress = false;
export function isManualLogoutInProgress() {
    return signOutNavigationInProgress;
}
export function AppAuthProvider({ children, config }) {
    const { appName, getToken, setToken: storeToken, clearAuth, fetchUser: fetchUserFn, onLogout, skipDemoTokens = false, extraKeysToClearOnTenantSwitch = [], } = config;
    // Stable ref so effects always see the latest config without re-running
    const configRef = useRef(config);
    configRef.current = config;
    const TOKEN_KEY = `${appName}:authToken`;
    const IMPERSONATION_TOKEN_KEY = `${appName}:impersonationToken`;
    // ---- State ---------------------------------------------------------------
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Impersonation
    const [impersonation, setImpersonation] = useState(() => {
        const storedToken = localStorage.getItem(IMPERSONATION_TOKEN_KEY);
        if (storedToken) {
            const info = getImpersonationInfo(storedToken);
            if (info && info.expiresAt > Math.floor(Date.now() / 1000)) {
                return info;
            }
            // Expired — clean up
            localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
            if (info) {
                const authServiceUrl = getServiceApiUrl('auth').replace(/\/api$/, '');
                fetch(`${authServiceUrl}/api/admin/impersonation/end`, {
                    method: 'POST',
                    signal: authRequestSignal(),
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token: storedToken, exit_reason: 'token_expired' }),
                }).catch(() => { });
            }
        }
        return null;
    });
    // Token refresh
    const refreshTimerRef = useRef(null);
    /** Fires when an impersonation token expires, to hand the admin back their session. */
    const impersonationTimerRef = useRef(null);
    /**
     * The renewal currently running, if any. `reconcileWithCookie` waits on this
     * rather than bailing: a renewal already derives from the cookie, so once it
     * settles the stored token is exactly what a reconcile would have produced.
     * Bailing instead made the IDENTITY_MISMATCH retry a no-op whenever a renewal
     * happened to be in flight — the one window it matters in.
     */
    const refreshInFlightRef = useRef(null);
    // Mount tracking for the fetchUser retry loop, so background retries stop
    // promptly if the provider unmounts (HMR, navigation away, etc.).
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    /**
     * Last input event seen in this tab, and the stamp we last told the server about.
     *
     * Starts at 0, not `Date.now()`: mounting is not input. Seeding it with the
     * current time made the very first tick report presence unconditionally, so any
     * tab that merely *loaded* — a bfcache restore, an automated reload, a redirect
     * chain — bought the session one free extension with nobody there. That is the
     * exact shape I-12 exists to prevent, arrived at from the client side.
     */
    const lastActivityRef = useRef(0);
    const activityReportedAtRef = useRef(0);
    /**
     * When the server last vouched for this tab's session, or a focus check last
     * asked it to. Starts at mount so the first check waits a minute: the bootstrap
     * is asking the same question then. Stamping the ask as well keeps the `focus`
     * and `visibilitychange` that both fire on one return to a single call.
     */
    const sessionConfirmedAtRef = useRef(Date.now());
    /**
     * A focus check was told this tab's session is revoked; the next tick must renew
     * — adopting a newer session from the cookie, or ending the tab.
     */
    const focusCheckDueRef = useRef(false);
    /** Not before this may the heavy claims path be tried again after a failure. */
    const claimsRetryAtRef = useRef(0);
    /**
     * The heavy path declined to re-derive this token's claims (the workspace it
     * names is no longer the user's). While set, the cheap path must not renew
     * either: it copies the very claims that were refused, and a token renewed that
     * way never expires — the user keeps access the server just said they lost.
     */
    const claimsRefusedRef = useRef(false);
    /** Set once the server has refused this session; every renewal path stands down. */
    const signingOutRef = useRef(false);
    /** The delayed finish of a refused session, so an unmount can cancel it. */
    const finishTimerRef = useRef(null);
    /** The server's idle deadline, mirrored into state for consumers that render it. */
    const idleDeadlineRef = useRef(null);
    const [idleExpiresAt, setIdleExpiresAt] = useState(null);
    /**
     * Whether the server is actually refusing idle sessions yet.
     *
     * False through the rollout window, when a deadline is reported but nothing acts
     * on it. The warning is gated on this so a returning user does not watch a
     * countdown reach zero and then see nothing happen — a false alarm is how people
     * learn to ignore the real one.
     */
    const [idleEnforced, setIdleEnforced] = useState(false);
    /** The same flag where the tick can read it without re-creating the callback. */
    const idleEnforcedRef = useRef(false);
    /** Is the deadline we know of already past, with a server that will act on it? */
    const deadlineOverdue = () => {
        if (!idleEnforcedRef.current || !idleDeadlineRef.current)
            return false;
        const at = Date.parse(idleDeadlineRef.current);
        return Number.isFinite(at) && at <= Date.now();
    };
    /**
     * Adopt the idle deadline reported alongside a user fetch or a renewal.
     *
     * Every path that talks to auth funnels through here, because the ways of *not*
     * learning the deadline all look harmless in isolation: an ordinary reload with a
     * valid token renews nothing, so seeding it only where a renewal happens left the
     * common case paced off token expiry — hours — against a thirty-minute threshold,
     * with no warning shown either, since the countdown had nothing to count to.
     */
    const adoptIdleDeadline = (deadline) => {
        if (!deadline)
            return;
        // Impersonation is deliberately never renewed, and the tick is never scheduled
        // for it — so adopting a deadline would arm a countdown nothing can extend: the
        // toast appears, "Stay signed in" fails every time because renewal short-circuits
        // to `impersonating`, and the countdown reaches zero with nothing happening.
        if (isLiveImpersonation(configRef.current.getToken()))
            return;
        const changed = idleDeadlineRef.current !== deadline;
        idleDeadlineRef.current = deadline;
        // A response that lands after unmount must not arm anything: the tick it armed
        // would reschedule itself on a dead instance for ever, beside the live one.
        if (!isMountedRef.current)
            return;
        setIdleExpiresAt(deadline);
        // Re-pace, don't just record. `scheduleTokenRefresh` reads the deadline once, at
        // scheduling time, so learning one later left whatever timer was already armed —
        // six hours, if it was armed before any deadline was known. The countdown became
        // correct while the tab still reported nothing, which is the worst of both.
        //
        // And when the deadline we just learned is already behind us and the server
        // enforces it, ask now rather than at the next tick: a reload onto an idle
        // session otherwise rendered the whole app for a minute before the tick found
        // out. Callers set `idleEnforcedRef` before calling this for exactly that reason.
        const overdue = deadlineOverdue();
        if ((changed || overdue) && scheduleTokenRefreshRef.current) {
            const token = configRef.current.getToken();
            if (token)
                scheduleTokenRefreshRef.current(token, undefined, overdue ? 0 : undefined);
        }
    };
    /** Set below; `adoptIdleDeadline` is declared before the scheduler exists. */
    const scheduleTokenRefreshRef = useRef(null);
    // Presence is observed here because only the browser can distinguish a person
    // from a timer: app request traffic cannot (apps poll), and the renewal timer
    // cannot (it fires regardless). Listeners are passive and throttled — these
    // events arrive in floods and all we need is a coarse "recently".
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        let throttled = false;
        const stamp = () => {
            if (throttled)
                return;
            throttled = true;
            lastActivityRef.current = Date.now();
            window.setTimeout(() => {
                throttled = false;
            }, ACTIVITY_STAMP_THROTTLE_MS);
        };
        for (const event of ACTIVITY_EVENTS) {
            window.addEventListener(event, stamp, { passive: true });
        }
        return () => {
            for (const event of ACTIVITY_EVENTS)
                window.removeEventListener(event, stamp);
        };
    }, []);
    // Keep the caller's extra-keys array in a ref so the callbacks below stay
    // stable across renders (the prop defaults to a fresh [] every render).
    const extraKeysRef = useRef(extraKeysToClearOnTenantSwitch);
    useEffect(() => {
        extraKeysRef.current = extraKeysToClearOnTenantSwitch;
    });
    // ---- Callbacks -----------------------------------------------------------
    /**
     * Drop scope state that belonged to the previous identity or org. Called
     * whenever the identity behind the stored token changes, so a new user cannot
     * inherit the previous one's workspace selection.
     */
    const clearScopeKeys = useCallback(() => {
        clearScopeKeysFor(appName, extraKeysRef.current);
    }, [appName]);
    /**
     * Refresh the JWT token. The derivation lives in `renewSessionToken` so the
     * provider and the app-side `ActivityTracker` renewals cannot drift apart —
     * they did, and the drift left ID-8 open outside this file.
     */
    const refreshToken = useCallback((currentToken) => {
        if (refreshInFlightRef.current)
            return refreshInFlightRef.current;
        const run = (async () => {
            try {
                const result = await renewSessionToken({
                    currentToken,
                    appName,
                    extraKeysToClear: extraKeysRef.current,
                });
                if (result.status !== 'renewed')
                    return null;
                // Keep the deadline this renewal reported. Bootstrap goes through here, and
                // without it the tick has no idea when the session goes idle and paces
                // itself purely off token expiry — six hours in prod, against a
                // thirty-minute threshold, which would sign an actively-working user out.
                if (result.idleExpiresAt !== null) {
                    idleDeadlineRef.current = result.idleExpiresAt;
                    if (isMountedRef.current)
                        setIdleExpiresAt(result.idleExpiresAt);
                }
                configRef.current.setToken(result.token);
                return result.token;
            }
            finally {
                refreshInFlightRef.current = null;
            }
        })();
        refreshInFlightRef.current = run;
        return run;
    }, [appName]);
    /**
     * End the session in this tab because the server said it is over.
     *
     * No `POST /auth/logout` — there is nothing left to invalidate, the server has
     * already refused the credential. The suite sweep and the broadcast still run,
     * so sibling tabs on this origin learn immediately rather than each waiting for
     * its own tick (ID-7).
     */
    const endRefusedSession = useCallback((reason) => {
        // Once. A second refusal can land inside the explanation window — a focus
        // re-check adopting the same overdue deadline asks the server immediately, a
        // "Stay signed in" click already in flight resolves — and each used to post
        // another logout, broadcast another sign-out and navigate again. And never on
        // an instance that has unmounted: a renewal that resolves after unmount would
        // otherwise sweep the suite and navigate the window its replacement owns.
        if (!isMountedRef.current || signingOutRef.current)
            return;
        signingOutRef.current = true;
        // Claim the navigation before `setUser(null)` below, or ProtectedRoute
        // redirects during that re-render and the reason never reaches the login page.
        signOutNavigationInProgress = true;
        // An idle refusal was about *renewal*. The cookie and the `sessions` row are
        // still there, and only the renewal paths consult `last_used` — so the cookie
        // would go on authenticating /session/me, /session/list,
        // /session/revoke-all-others and the private-file middleware for the rest of
        // its lifetime. The per-app code this replaced carried exactly this warning:
        // without invalidating server-side, "the user can re-authenticate by
        // navigating back". Best-effort and unawaited: the local sign-out below must
        // happen either way.
        //
        // A *revoked* refusal means the row is already gone, so there is nothing to
        // invalidate — and asking anyway is dangerous: the cookie may by now name a
        // different, live session (the user signed out elsewhere and back in), and a
        // logout carrying both credentials used to end whichever one the cookie named.
        if (reason === 'inactivity') {
            try {
                const authApiUrl = getServiceApiUrl('auth');
                const headers = { 'Content-Type': 'application/json' };
                const token = configRef.current.getToken();
                if (token)
                    headers['Authorization'] = `Bearer ${token}`;
                const csrfToken = getCsrfToken();
                if (csrfToken)
                    headers['x-csrf-token'] = csrfToken;
                void fetch(`${authApiUrl}/auth/logout`, {
                    method: 'POST',
                    headers,
                    credentials: 'include',
                    signal: authRequestSignal(),
                }).catch(() => { });
            }
            catch {
                // No network, no fetch: nothing to invalidate remotely, carry on locally.
            }
        }
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        try {
            window.dispatchEvent(new CustomEvent('session:logout-start', { detail: { reason } }));
            if (reason === 'inactivity') {
                // Survives bfcache but not a new tab, so a restored page can explain
                // itself instead of looking like it logged the user out for no reason.
                sessionStorage.setItem(`${appName}:loggedOutInactivity`, '1');
            }
        }
        catch {
            // Storage or events unavailable: the sign-out below still has to happen.
        }
        if (isMountedRef.current) {
            setUser(null);
            setImpersonation(null);
        }
        // `onLogout` assigns `window.location.href`. Called synchronously it navigates
        // in the same tick, so React never renders the "signing out" popover that the
        // event above exists to trigger — the old path only appeared to work because it
        // awaited a logout request first. A brief delay lets the explanation paint; the
        // credential is already dead server-side, so nothing is held open by waiting.
        //
        // The local credential goes at the *end* of that delay, not the start. It is
        // refused for renewal, but app backends verify the JWT alone, so it still
        // answers any request a provider fires during the explanation. Cleared up
        // front, those requests went out with no header, got 401, and the app's own
        // interceptor navigated to a bare login page first — no reason, no way back,
        // and the popover this delay exists for never shown. The suite sweep and the
        // broadcast move with it; a sibling tab learns 1.2s later, which is nothing.
        const finish = () => {
            configRef.current.clearAuth();
            signOutOfSuite(reason);
            configRef.current.onLogout(reason);
        };
        if (typeof window === 'undefined')
            finish();
        else
            finishTimerRef.current = window.setTimeout(finish, SIGN_OUT_EXPLANATION_MS);
    }, [appName]);
    /**
     * The session tick: one timer at a fixed cadence that decides, each time it fires,
     * whether there is anything to say to the server.
     *
     * **Why a fixed cadence rather than a computed delay.** The previous version
     * computed one delay from the token's expiry, the idle deadline, a report margin,
     * an in-margin poll rate and two floors, combined with nested `min`/`max`. Every
     * review round found a new hole in it and every fix opened another: a `max` that
     * should have been a `min` put the last call *on* the deadline; a floor applied
     * afterwards put it there again; a passed deadline fell back to six-hour pacing; a
     * freshness bound meant to stop over-claiming silently dropped real input. Five
     * interacting clamps, each correct in isolation.
     *
     * A fixed cadence has no arithmetic to get wrong. The timer is dumb; the decision
     * is made from current state at the moment it fires, as a list of independent
     * reasons to call. The cost is a comparison a minute per tab, which is nothing —
     * the old per-tab `ActivityTracker` did this every *second* — and the network cost
     * is unchanged, because it is the reasons, not the timer, that decide whether a
     * request goes out.
     *
     * It still fires whether or not anyone is present: that is how an untouched tab
     * learns the session ended. But it only *extends* the session when the browser has
     * real input to report (I-12).
     */
    const scheduleTokenRefresh = useCallback((currentToken, idleExpiresAtHint, delayMs = SESSION_TICK_INTERVAL_SECONDS * 1000) => {
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        // Never arm on an unmounted instance. The init cleanup clears the pending timer,
        // but a tick already awaiting its renewal reschedules when it resolves — and
        // that timer would outlive the provider, ticking beside its replacement.
        if (!isMountedRef.current || signingOutRef.current)
            return;
        // A demo token is not a session and has nothing to renew. The bootstrap skips
        // it on purpose; the deadline adoption paths reach here with whatever the app
        // holds, so the same rule has to hold here.
        if (currentToken.startsWith('demo-token-'))
            return;
        // A revoked session is waiting to be settled: whoever re-arms — the focus check,
        // or a tick that was mid-renewal when the answer came — comes straight back.
        // Left to each caller, the tick's normal-cadence re-arm replaced the focus
        // check's immediate one, and the revoked tab kept working a full interval.
        if (focusCheckDueRef.current)
            delayMs = 0;
        if (idleExpiresAtHint) {
            // Only ever adopt a real deadline. Writing the hint unconditionally let a
            // renewal that reported `null` wipe a deadline the tab already knew.
            idleDeadlineRef.current = idleExpiresAtHint;
            setIdleExpiresAt(idleExpiresAtHint);
        }
        // Deliberately no early return for an already-expired token: `/session/activity`
        // accepts one so a tab the browser froze can rejoin when a sibling kept the
        // session alive. An earlier draft returned here and that healing path became
        // unreachable, ending renewal for the tab entirely.
        // The decision, separated from the timer so that a throw anywhere in it cannot
        // end renewal for the tab: every path below re-arms, but only if it is reached.
        const tick = async (token) => {
            if (signingOutRef.current)
                return;
            const now = Date.now();
            const secondsUntilExpiry = getSecondsUntilExpiry(token);
            const deadlineMs = idleDeadlineRef.current ? Date.parse(idleDeadlineRef.current) : NaN;
            const secondsUntilIdle = Number.isFinite(deadlineMs) ? (deadlineMs - now) / 1000 : NaN;
            const unreportedInput = lastActivityRef.current > activityReportedAtRef.current;
            const secondsSinceReport = (now - activityReportedAtRef.current) / 1000;
            // Independent reasons to call, each one readable on its own.
            const tokenAging = secondsUntilExpiry <= refreshThresholdSeconds(token);
            // A deadline already in the past satisfies "close" too, which is right only
            // when the server will actually refuse: then one call ends the session. With
            // enforcement off nothing refuses and nothing moves the clock, so the deadline
            // stays fixed in the past and this stayed true for ever — every idle tab
            // calling once a minute for days, each call rotating cookies and pushing
            // `expires_at` out, for tabs nobody was using.
            const deadlineClose = Number.isFinite(secondsUntilIdle) &&
                secondsUntilIdle <= IDLE_REPORT_MARGIN_SECONDS &&
                (secondsUntilIdle > 0 || idleEnforcedRef.current);
            const presenceDue = unreportedInput && secondsSinceReport >= ACTIVITY_REPORT_INTERVAL_SECONDS;
            const claimsDerivedAt = claimsDerivedAtMs(token);
            const claimsDue = claimsDerivedAt !== null && now - claimsDerivedAt > CLAIM_REFRESH_INTERVAL_MS;
            const claimsStale = (claimsDue || claimsRefusedRef.current) && now >= claimsRetryAtRef.current;
            // Not a renewal need: the focus check (`/auth/session/me`) said this tab's
            // session is gone. The renewal is what settles it — adopting a newer session
            // the cookie names, or ending the tab — because its refusal handling already
            // exists.
            // Cleared only once this tick renews: a tick that defers to an in-flight
            // renewal below has not acted on it yet.
            const focusCheckDue = focusCheckDueRef.current;
            // A refused re-derivation suspends renewal altogether until the heavy path
            // is due again: the cheap path would copy forward the claims the server just
            // declined, and a token kept alive that way never expires. Left alone, this
            // one ages out on its own `exp`, which is what happened before the cheap
            // path existed.
            // A revoked session is past suspending: whatever the renewal does next (adopt
            // the cookie's session, or refuse) cannot carry the refused claims forward.
            if (claimsRefusedRef.current && !claimsStale && !focusCheckDue) {
                scheduleTokenRefresh(token);
                return;
            }
            if (!tokenAging && !deadlineClose && !presenceDue && !claimsStale && !focusCheckDue) {
                scheduleTokenRefresh(token);
                return;
            }
            // Share the in-flight slot with `refreshToken` and `reportActivity`, so a tick
            // and a "Stay signed in" click cannot both renew — the tick read `last_used`
            // before the click's write, so its deadline is the older one, and whichever
            // resolved last won.
            if (refreshInFlightRef.current) {
                await refreshInFlightRef.current;
                const after = configRef.current.getToken();
                // A new token is one the server just issued, so a revocation seen for the
                // old one no longer applies. The same token (the renewal was unreachable,
                // or adopted nothing) keeps it due, and the re-arm comes straight back.
                if (after !== token)
                    focusCheckDueRef.current = false;
                if (after)
                    scheduleTokenRefresh(after);
                return;
            }
            focusCheckDueRef.current = false;
            // The heavier path re-derives role and workspace membership from Dashboard. It
            // reports no presence, which is fine: the input stays unreported and the next
            // tick sends it, carrying its true age.
            const run = claimsStale
                ? renewSessionToken({ currentToken: token, appName, extraKeysToClear: extraKeysRef.current })
                : renewSessionToken({
                    currentToken: token,
                    appName,
                    extraKeysToClear: extraKeysRef.current,
                    activity: unreportedInput,
                    // How old the input is, so the server records when it happened rather
                    // than when this request arrived. Neither over-claiming nor discarding.
                    ...(unreportedInput && { activityAgeMs: now - lastActivityRef.current }),
                });
            refreshInFlightRef.current = run.then((r) => (r.status === 'renewed' ? r.token : null));
            let result;
            try {
                result = await run;
            }
            finally {
                refreshInFlightRef.current = null;
            }
            // Whatever the answer was, the heavy path is not asked again for a while:
            // the cheap one keeps the credential alive in the meantime, and the claims
            // are re-derived on the next reload regardless.
            if (claimsStale && result.status !== 'renewed') {
                claimsRetryAtRef.current = Date.now() + CLAIM_REFRESH_RETRY_MS;
            }
            if (result.status === 'forbidden') {
                claimsRefusedRef.current = true;
                console.warn(`[${appName}] Claims could not be re-derived; renewal suspended until the next attempt`);
                scheduleTokenRefresh(token);
                return;
            }
            if (result.status === 'renewed') {
                sessionConfirmedAtRef.current = Date.now();
                if (claimsStale) {
                    claimsRefusedRef.current = false;
                    // Re-derived claims are only half the story: the token's role changed, so
                    // the API behaves differently, but the mounted `user` object still drives
                    // what is on screen. The old scheduled refresh re-fetched after every
                    // renewal; dropping that left a tab rendering a stale role indefinitely.
                    // Only on this path — the cheap one copies claims forward, so there is
                    // nothing new to show.
                    refreshUserRef.current?.();
                }
                else if (unreportedInput) {
                    activityReportedAtRef.current = Date.now();
                }
                configRef.current.setToken(result.token);
                scheduleTokenRefresh(result.token, result.idleExpiresAt);
                return;
            }
            if (result.status === 'idle') {
                endRefusedSession('inactivity');
                return;
            }
            if (result.status === 'unauthenticated') {
                endRefusedSession('revoked');
                return;
            }
            // `unreachable` says nothing about the credential and `impersonating` means
            // there was nothing to do. Neither is a reason to sign anyone out (I-8) — keep
            // the loop alive and ask again on the next tick.
            scheduleTokenRefresh(token);
        };
        refreshTimerRef.current = window.setTimeout(async () => {
            // This timer has fired, so the slot is free. Without clearing it here a retry
            // below saw a non-null ref and skipped, leaving the tab with no timer at all.
            refreshTimerRef.current = null;
            const token = configRef.current.getToken();
            if (!token)
                return;
            try {
                await tick(token);
            }
            catch (err) {
                // The renewal helper reports failures as statuses rather than throwing, so
                // reaching here means something app-supplied did — `setToken` against a
                // full storage quota, say. One bad tick must not end renewal for the tab:
                // with enforcement on, that signs out a user who is working continuously.
                // Re-arm unless a path above already did, or the session was ended.
                console.error(`[${appName}] Session tick failed; will try again`, err);
                if (refreshTimerRef.current === null && configRef.current.getToken()) {
                    scheduleTokenRefresh(token);
                }
            }
        }, delayMs);
    }, [appName, endRefusedSession]);
    // `adoptIdleDeadline` is declared above the scheduler, so it reaches it through
    // this ref rather than a forward reference.
    scheduleTokenRefreshRef.current = scheduleTokenRefresh;
    /**
     * Report presence immediately — the "Stay signed in" affordance.
     *
     * Returns the deadline the server came back with, or null. A null must leave the
     * caller's warning on screen: claiming an extension that did not happen is worse
     * than showing the warning a moment longer.
     */
    const reportActivity = useCallback(async () => {
        const token = configRef.current.getToken();
        if (!token || signingOutRef.current)
            return null;
        lastActivityRef.current = Date.now();
        // Wait for any renewal already running rather than racing it: two concurrent
        // renewals report two deadlines, and the older one landing last would undo the
        // extension the user just asked for.
        if (refreshInFlightRef.current) {
            try {
                await refreshInFlightRef.current;
            }
            catch {
                // Its own caller owns that failure; we only needed it out of the way.
            }
        }
        const run = renewSessionToken({
            currentToken: configRef.current.getToken() ?? token,
            appName,
            extraKeysToClear: extraKeysRef.current,
            activity: true,
        });
        refreshInFlightRef.current = run.then((r) => (r.status === 'renewed' ? r.token : null));
        let result;
        try {
            result = await run;
        }
        finally {
            refreshInFlightRef.current = null;
        }
        if (result.status !== 'renewed') {
            if (result.status === 'idle')
                endRefusedSession('inactivity');
            else if (result.status === 'unauthenticated')
                endRefusedSession('revoked');
            return null;
        }
        activityReportedAtRef.current = Date.now();
        configRef.current.setToken(result.token);
        scheduleTokenRefresh(result.token, result.idleExpiresAt);
        return result.idleExpiresAt;
    }, [appName, endRefusedSession, scheduleTokenRefresh]);
    /**
     * Renew a token the app's own backend refused with a plain 401 (see
     * `tokenRecovery.ts`). Concurrent refusals share one attempt, a renewal
     * already running is waited on rather than raced, and a refusal of a token
     * that has since been replaced needs no renewal at all — without that, each
     * of a page's parallel requests that lands after the first recovery settles
     * would run the heavy re-derivation again.
     */
    const recoveryInFlightRef = useRef(null);
    /** How many `doFetchUser` loops are running (see the refresh after renewal). */
    const fetchingUserRef = useRef(0);
    const recoverRefusedToken = useCallback((refusedToken) => {
        const stored = configRef.current.getToken();
        if (refusedToken && stored && stored !== refusedToken)
            return Promise.resolve('renewed');
        if (recoveryInFlightRef.current)
            return recoveryInFlightRef.current;
        // Signing out, or no longer mounted: whatever ended it owns the outcome, and
        // a token stored now would outlive the sign-out. Checked after every wait.
        const ending = () => signingOutRef.current || !isMountedRef.current;
        const run = (async () => {
            if (ending())
                return 'ended';
            const refused = configRef.current.getToken();
            // Nothing to renew from. Not proof of a sign-out — a call can fire before
            // bootstrap stores a token — so the 401 stands and nothing is ended here.
            if (!refused)
                return 'refused';
            // Never re-derive a live impersonation away (the cookie names the admin).
            if (isLiveImpersonation(refused))
                return 'refused';
            if (refreshInFlightRef.current) {
                try {
                    await refreshInFlightRef.current;
                }
                catch {
                    // Its own caller owns that failure.
                }
                if (ending())
                    return 'ended';
                // That renewal already replaced the refused token: nothing left to do.
                const current = configRef.current.getToken();
                if (current && current !== refused)
                    return 'renewed';
            }
            const attempt = renewSessionToken({
                currentToken: configRef.current.getToken() ?? refused,
                appName,
                extraKeysToClear: extraKeysRef.current,
            });
            refreshInFlightRef.current = attempt.then((r) => (r.status === 'renewed' ? r.token : null));
            let result;
            try {
                result = await attempt;
            }
            finally {
                refreshInFlightRef.current = null;
            }
            if (ending())
                return 'ended';
            if (result.status === 'renewed') {
                if (result.idleExpiresAt !== null) {
                    idleDeadlineRef.current = result.idleExpiresAt;
                    if (isMountedRef.current)
                        setIdleExpiresAt(result.idleExpiresAt);
                }
                // The same bookkeeping the tick does after this (heavy) renewal: the
                // session is confirmed live, and claims are freshly derived, so a
                // suspension over refused claims no longer applies.
                sessionConfirmedAtRef.current = Date.now();
                claimsRefusedRef.current = false;
                focusCheckDueRef.current = false;
                configRef.current.setToken(result.token);
                scheduleTokenRefresh(result.token, result.idleExpiresAt);
                // A changed user or org already re-fetches (IDENTITY_ADOPTED_EVENT). A
                // changed role or workspace inside the same session does not, and the
                // mounted user would go on showing the old one.
                const before = decodeJwtPayload(refused);
                const after = decodeJwtPayload(result.token);
                // Not while a user fetch is already running: it is retrying with this
                // token anyway (recovery is often called from inside it), and a second
                // loop would race it to `setUser`.
                const claimsChanged = before?.role !== after?.role || before?.workspaceId !== after?.workspaceId;
                if (claimsChanged && fetchingUserRef.current === 0) {
                    void refreshUserRef.current?.();
                }
                return 'renewed';
            }
            if (result.status === 'idle') {
                endRefusedSession('inactivity');
                return 'ended';
            }
            if (result.status === 'unauthenticated') {
                endRefusedSession('revoked');
                return 'ended';
            }
            if (result.status === 'unreachable')
                return 'unreachable';
            // `forbidden`: the server will not re-derive this token's claims. As in the
            // tick, that is not "the session is over" — nothing is signed out here.
            return 'refused';
        })();
        recoveryInFlightRef.current = run;
        void run.finally(() => {
            if (recoveryInFlightRef.current === run)
                recoveryInFlightRef.current = null;
        });
        return run;
    }, [appName, endRefusedSession, scheduleTokenRefresh]);
    /**
     * The app's backend refused even a token recovery could not improve on — a
     * replay of a freshly renewed token, say. End the session with a reason
     * rather than leave the client to clear its token behind the provider's back,
     * which left a tab that looked signed in while every call failed.
     */
    const endUnusableSession = useCallback(() => {
        endRefusedSession('revoked');
    }, [endRefusedSession]);
    useEffect(() => registerTokenRecovery(recoverRefusedToken, endUnusableSession), [recoverRefusedToken, endUnusableSession]);
    /**
     * Hand an admin back to their own session when impersonation runs out.
     *
     * An impersonation token lives an hour and is deliberately never renewed. With
     * no timer at all the tab just starts 401ing, `doFetchUser` clears auth, and
     * the admin is dropped at the login page — from a session that ended normally.
     * The session cookie still names the admin the whole time, so the honest
     * ending is to re-derive from it at expiry. `reconcileWithCookie` does exactly
     * that once `isLiveImpersonation` stops covering the token.
     *
     * Swapping the token back is only half of it: the impersonation *record* —
     * `{app}:impersonationToken` and the `impersonation` state behind the banner —
     * has to go with it, or the admin sits on their own session while the UI still
     * says they are someone else and still offers "Exit impersonation". Cleared
     * only once the exchange has actually replaced the token, so a failed handback
     * leaves an honest (if broken) impersonation state rather than a banner-less
     * dead token.
     */
    const scheduleImpersonationHandback = useCallback((impersonationToken) => {
        if (impersonationTimerRef.current) {
            window.clearTimeout(impersonationTimerRef.current);
            impersonationTimerRef.current = null;
        }
        const info = getImpersonationInfo(impersonationToken);
        if (!info?.expiresAt)
            return;
        // A second past expiry, so the token is unambiguously dead when we look.
        const msUntilHandback = (info.expiresAt + 1) * 1000 - Date.now();
        if (msUntilHandback <= 0)
            return;
        console.log(`[${appName}] Impersonation ends in ${Math.round(msUntilHandback / 60000)} min; will return to the admin session`);
        impersonationTimerRef.current = window.setTimeout(() => {
            void (async () => {
                await reconcileWithCookieRef.current?.(true);
                // Did the exchange actually land? If the cookie was unreachable the
                // stored token is still the (now dead) impersonation token, and tearing
                // the record down would leave no way to read what state the tab is in.
                const current = configRef.current.getToken();
                if (current && getImpersonationInfo(current))
                    return;
                localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
                if (isMountedRef.current)
                    setImpersonation(null);
                // Close the impersonation record server-side. The mount-time cleanup in
                // the `impersonation` initializer does this when it finds an expired
                // token; removing the key here means that never runs, so the end event
                // would otherwise be lost for a tab that stayed open.
                const authServiceUrl = getServiceApiUrl('auth').replace(/\/api$/, '');
                fetch(`${authServiceUrl}/api/admin/impersonation/end`, {
                    method: 'POST',
                    signal: authRequestSignal(),
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token: impersonationToken, exit_reason: 'token_expired' }),
                }).catch(() => { });
            })();
        }, msUntilHandback);
    }, [appName, IMPERSONATION_TOKEN_KEY]);
    /**
     * Fetch user via the app-supplied callback.
     *
     * Retries transient failures (network timeout, 5xx, etc.) with exponential
     * backoff so a slow network on page refresh doesn't kick the user to
     * /login. The bootstrap caller `await`s this, so `isLoading` stays true
     * during retries — `ProtectedRoute` keeps the spinner visible instead of
     * triggering a redirect.
     *
     * Definitive failures (401 / explicit logout signal) short-circuit the
     * loop and clear auth as before.
     */
    const fetchUserWithRetry = useCallback(async () => {
        const MAX_ATTEMPTS = 10;
        let backoff = 1000;
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            if (!isMountedRef.current)
                return null;
            try {
                const result = await configRef.current.fetchUser();
                if (!isMountedRef.current)
                    return null;
                if (result) {
                    setUser(result);
                    return result;
                }
                // null without throw — auth-related, do not retry.
                setUser(null);
                return null;
            }
            catch (error) {
                const status = error?.statusCode ?? error?.response?.status;
                if (status === 401) {
                    console.error(`[${appName}] 401 fetching user — clearing auth`);
                    // Already signing out (a token recovery ended the session): leave the
                    // clear to `endRefusedSession`, which holds the token through the
                    // explanation window on purpose.
                    if (!signingOutRef.current)
                        configRef.current.clearAuth();
                    if (isMountedRef.current)
                        setUser(null);
                    return null;
                }
                const isLastAttempt = attempt === MAX_ATTEMPTS - 1;
                if (isLastAttempt) {
                    console.error(`[${appName}] fetchUser failed after ${MAX_ATTEMPTS} attempts:`, error?.message ?? error);
                    if (isMountedRef.current)
                        setUser(null);
                    return null;
                }
                console.warn(`[${appName}] Transient fetchUser error (attempt ${attempt + 1}/${MAX_ATTEMPTS}), retrying in ${backoff}ms:`, error?.message ?? error);
                await new Promise((r) => setTimeout(r, backoff));
                backoff = Math.min(backoff * 2, 30000);
            }
        }
        return null;
    }, [appName]);
    /** Counted, so a token recovery can tell a user fetch is already running. */
    const doFetchUser = useCallback(async () => {
        fetchingUserRef.current += 1;
        try {
            return await fetchUserWithRetry();
        }
        finally {
            fetchingUserRef.current -= 1;
        }
    }, [fetchUserWithRetry]);
    /** Public refreshUser (no token change, just re-fetch profile). */
    const refreshUser = useCallback(async () => {
        const token = configRef.current.getToken();
        if (token) {
            await doFetchUser();
        }
    }, [doFetchUser]);
    // Keep a ref so the timer callback can call the latest version.
    const refreshUserRef = useRef(refreshUser);
    useEffect(() => { refreshUserRef.current = refreshUser; }, [refreshUser]);
    // ---- Identity re-check ---------------------------------------------------
    // The bootstrap above only runs on mount. A tab left open across a sign-in
    // elsewhere in the suite would otherwise keep renewing the superseded
    // identity for as long as it stays open, because the session cookie is shared
    // across .airclou.com while each app's stored token is not.
    const lastIdentityCheckRef = useRef(0);
    /** Coalesces concurrent checks: several parallel API calls can each report a
     *  mismatch, and each report bypasses the throttle. Without this they would
     *  stampede /auth/me and /auth/refresh. */
    const identityCheckInFlightRef = useRef(null);
    /** Set below; the handback timer is declared before `reconcileWithCookie` exists. */
    const reconcileWithCookieRef = useRef(null);
    /**
     * Resolves true when the cookie's answer shows the tab's token names a live
     * session — the cookie names that same session, or the tab just adopted the
     * cookie's. A live cookie session proves its row exists, so the focus check
     * needs no second call. False means "not known", never "revoked".
     */
    const reconcileWithCookie = useCallback(async (force = false) => {
        if (!force && Date.now() - lastIdentityCheckRef.current < IDENTITY_RECHECK_MIN_INTERVAL_MS)
            return false;
        // A check already running answers this caller too.
        if (identityCheckInFlightRef.current)
            return identityCheckInFlightRef.current;
        // A renewal already in flight is *usually* asking the cookie, so its answer
        // is this caller's answer too. Wait for it rather than bailing — a caller
        // that is about to retry a refused request needs the new token to exist.
        // But the renewal may have taken the Bearer-fallback leg (a transiently
        // unreachable or CSRF-refused cookie leg) and renewed the stale identity. A
        // forced reconcile exists precisely to answer "who does the cookie name?",
        // so it keeps going and asks /auth/me itself; a routine one trusts the
        // renewal.
        if (refreshInFlightRef.current) {
            await refreshInFlightRef.current;
            if (!force)
                return false;
        }
        const storedToken = configRef.current.getToken();
        if (!storedToken || storedToken.startsWith('demo-token-'))
            return false;
        // Impersonation is a deliberate two-identity state (see refreshToken).
        if (isLiveImpersonation(storedToken))
            return false;
        const localClaims = decodeJwtPayload(storedToken);
        const localSub = localClaims?.sub ?? null;
        const localTenantId = localClaims?.tenantId ?? null;
        const localJti = localClaims?.jti ?? null;
        if (!localSub)
            return false;
        // Stamped only now that a check is actually happening: stamping above would
        // burn the throttle window on a call that bailed without asking anything.
        lastIdentityCheckRef.current = Date.now();
        const check = (async () => {
            const authApiUrl = getServiceApiUrl('auth');
            const response = await fetch(`${authApiUrl}/auth/me`, {
                credentials: 'include',
                signal: authRequestSignal(),
            });
            // No reachable cookie session (offline, cookie cleared, native shell):
            // leave the stored token alone rather than signing the user out.
            if (!response.ok)
                return false;
            const data = await response.json();
            // Cheapest place to learn it: this runs on focus and on mount, including the
            // ordinary reload path that renews nothing. The flag goes first, because
            // adopting an already-past deadline asks the server immediately only when the
            // tab knows the server will act on it. It is server config, not per session.
            if (typeof data.data?.session?.idleEnforced === 'boolean') {
                idleEnforcedRef.current = data.data.session.idleEnforced;
                setIdleEnforced(data.data.session.idleEnforced);
            }
            const cookieIdleExpiresAt = data.data?.session?.idleExpiresAt;
            const cookieUser = data.data?.user ?? data.user;
            const cookieSub = data.success ? cookieUser?.id : null;
            if (!cookieSub)
                return false;
            // Same comparison as bootstrap and `renewSessionToken`: sub AND tenantId.
            // Matching on sub alone misses a same-user org switch (select-tenant mints
            // a session with a new tenantId and the same sub), which would leave the
            // stale org in place until the next scheduled renewal — the exact window
            // this focus-time re-check exists to close.
            const cookieTenantId = cookieUser?.tenantId ?? null;
            const subChanged = cookieSub !== localSub;
            const tenantChanged = !!cookieTenantId && !!localTenantId && cookieTenantId !== localTenantId;
            // Same person and org, newer session (see the bootstrap): the stored
            // token's session is gone, so adopt the cookie's — keeping scope state.
            const cookieJti = data.data?.session?.jti ?? null;
            const sessionChanged = !!cookieJti && !!localJti && cookieJti !== localJti;
            if (!subChanged && !tenantChanged && !sessionChanged) {
                // The cookie's deadline is this tab's only when it names this tab's session.
                adoptIdleDeadline(cookieIdleExpiresAt);
                // Vouches for the stored token only when both name the same session row;
                // a jti-less token has no row for the cookie's answer to speak for.
                return !!cookieJti && cookieJti === localJti;
            }
            const scopeIsStale = subChanged || tenantChanged;
            console.log(scopeIsStale
                ? `[${appName}] Session cookie names a different ${subChanged ? 'user' : 'organization'} — adopting the cookie's ${subChanged ? 'identity' : 'organization'}`
                : `[${appName}] Session cookie names a newer session — adopting it`, { cookieSub, localSub, cookieTenantId, localTenantId });
            // Exchange first, clear second. A failed exchange must leave no trace:
            // clearing up front would strip the workspace selection while leaving the
            // user on the old identity.
            // The deadline follows the token: adopted only once the tab holds the
            // cookie's session. A failed exchange keeps the old token, and pacing it
            // against another session's deadline would warn about the wrong clock.
            const newToken = await exchangeCookieForToken();
            if (newToken && isMountedRef.current) {
                if (scopeIsStale)
                    clearScopeKeys();
                configRef.current.setToken(newToken);
                adoptIdleDeadline(cookieIdleExpiresAt);
                scheduleTokenRefresh(newToken);
                refreshUserRef.current?.();
                return true;
            }
            return false;
        })();
        identityCheckInFlightRef.current = check
            .catch(() => {
            // Network trouble is not evidence of a stale identity — leave it as-is.
            return false;
        })
            .finally(() => {
            identityCheckInFlightRef.current = null;
        });
        return identityCheckInFlightRef.current;
    }, [appName, clearScopeKeys, scheduleTokenRefresh]);
    useEffect(() => { reconcileWithCookieRef.current = reconcileWithCookie; }, [reconcileWithCookie]);
    useEffect(() => {
        // The reconcile asks who the *cookie* names and ignores a refusal, because it
        // cannot tell a signed-out cookie from being offline. This asks about the tab's
        // own session, whose refusal says exactly that it was revoked. It runs after
        // the reconcile so a newer session in the cookie is adopted first: its stored
        // token's session is gone too, and refusing it would sign out a live user.
        //
        // The one throttle for the focus path lives here. Stamped before the first
        // await, so the `focus` and `visibilitychange` that both fire on one return
        // make a single check.
        const onMaybeVisible = async () => {
            if (document.visibilityState !== 'visible')
                return;
            const due = Date.now() - sessionConfirmedAtRef.current >= FOCUS_SESSION_CHECK_MIN_INTERVAL_MS;
            if (due)
                sessionConfirmedAtRef.current = Date.now();
            const cookieVouched = await reconcileWithCookie();
            // The cookie named this tab's session and answered: its row is live.
            if (!due || cookieVouched)
                return;
            const token = configRef.current.getToken();
            // Impersonation is never renewed (see `renewSessionToken`), so escalating
            // would do nothing; it ends at its own handback.
            if (!token || token.startsWith('demo-token-') || isLiveImpersonation(token))
                return;
            if (!isMountedRef.current || signingOutRef.current)
                return;
            let response;
            try {
                // The session endpoint rather than `/auth/me`: this only needs "is the row
                // still there", not roles and preferences.
                response = await fetch(`${getServiceApiUrl('auth')}/auth/session/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                    credentials: 'omit',
                    signal: authRequestSignal(),
                });
            }
            catch {
                return; // No answer is not a revocation (I-8).
            }
            if (response.ok) {
                sessionConfirmedAtRef.current = Date.now();
                return;
            }
            if (response.status !== 401)
                return;
            const body = await response.json().catch(() => null);
            // Only this code. A bare 401 is usually an expired token, which the tick
            // renews through `/session/activity` — that route accepts one on purpose.
            if (body?.error?.code !== 'SESSION_REVOKED')
                return;
            if (!isMountedRef.current || signingOutRef.current)
                return;
            focusCheckDueRef.current = true;
            scheduleTokenRefresh(token, undefined, 0);
        };
        // An API client saw 401 IDENTITY_MISMATCH: check immediately, skipping the
        // throttle, and re-derive rather than logging the user out.
        const onMismatchReported = () => { void reconcileWithCookie(true); };
        // A renewal (the provider's own timer or ActivityTracker's backstop) derived
        // a different identity or org from the cookie. The token and scope keys are
        // already swapped by `renewSessionToken`; what's missing is the mounted app
        // — without a re-fetch, the chrome keeps showing the previous user while
        // every API call answers as the new one, and the reconcile above can never
        // heal it because the subs already match.
        const onIdentityAdopted = () => {
            const adoptedToken = configRef.current.getToken();
            if (adoptedToken) {
                // A different session, so the old deadline means nothing — clear it rather
                // than pacing and counting the new session down against the previous one's.
                // The next renewal or `/auth/me` reports the real one.
                idleDeadlineRef.current = null;
                setIdleExpiresAt(null);
                scheduleTokenRefresh(adoptedToken);
            }
            void refreshUserRef.current?.();
        };
        document.addEventListener('visibilitychange', onMaybeVisible);
        window.addEventListener('focus', onMaybeVisible);
        window.addEventListener(IDENTITY_MISMATCH_EVENT, onMismatchReported);
        window.addEventListener(IDENTITY_ADOPTED_EVENT, onIdentityAdopted);
        // The event says "go"; the registry lets a caller wait for "done" and retry
        // the request that was refused, instead of showing an error page whose only
        // escape is a reload.
        const unregister = registerIdentityReconciler(async () => { await reconcileWithCookie(true); });
        return () => {
            document.removeEventListener('visibilitychange', onMaybeVisible);
            window.removeEventListener('focus', onMaybeVisible);
            window.removeEventListener(IDENTITY_MISMATCH_EVENT, onMismatchReported);
            window.removeEventListener(IDENTITY_ADOPTED_EVENT, onIdentityAdopted);
            unregister();
        };
    }, [reconcileWithCookie, scheduleTokenRefresh]);
    // ---- Another tab signed out ----------------------------------------------
    useEffect(() => onSuiteSignOut((reason) => {
        // Clear locally, but do NOT re-broadcast: the sign-out already reached every
        // tab, and echoing it turns one sign-out into a storm.
        //
        // This flag matters as much here as in `logout`. Without it ProtectedRoute
        // wins the race and redirects with `?redirect=<this page>`, so the next
        // person to sign in on this machine lands on the previous user's page — from
        // a sign-out they performed deliberately, one tab over.
        signOutNavigationInProgress = true;
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        if (impersonationTimerRef.current) {
            window.clearTimeout(impersonationTimerRef.current);
            impersonationTimerRef.current = null;
        }
        // Announce it here too. The originating tab dispatches before signing out, but
        // a tab that learns through the channel never did — and the apps now suppress
        // their own dispatch for involuntary reasons on the assumption that one already
        // happened. So in every *other* tab the WebSocket was left open, presence was
        // never torn down server-side, and the connection banner stayed up through the
        // redirect. Before this change `onLogout()` always dispatched.
        try {
            window.dispatchEvent(new CustomEvent('session:logout-start', { detail: { reason: reason ?? 'revoked' } }));
        }
        catch {
            // Non-DOM environment: the sign-out below still has to happen.
        }
        configRef.current.clearAuth();
        clearSuiteAuthKeys();
        if (isMountedRef.current) {
            setUser(null);
            setImpersonation(null);
        }
        // Forward the reason the originating tab sent. Dropping it made every sibling
        // tab treat an idle sign-out as deliberate, which for helpdesk meant deleting
        // the user's unsent replies — work they never chose to abandon.
        configRef.current.onLogout(reason);
    }), []);
    // ---- Init effect ---------------------------------------------------------
    useEffect(() => {
        let cancelled = false;
        const initAuth = async () => {
            let currentToken = configRef.current.getToken();
            // -- Impersonation token from URL --------------------------------------
            const urlParams = new URLSearchParams(window.location.search);
            const impTokenFromUrl = urlParams.get(IMPERSONATION_TOKEN_PARAM);
            if (impTokenFromUrl) {
                const info = getImpersonationInfo(impTokenFromUrl);
                if (info && info.expiresAt > Math.floor(Date.now() / 1000)) {
                    configRef.current.setToken(impTokenFromUrl);
                    localStorage.setItem(IMPERSONATION_TOKEN_KEY, impTokenFromUrl);
                    localStorage.setItem(`${appName}:isRealUser`, 'true');
                    setImpersonation(info);
                    currentToken = impTokenFromUrl;
                }
                // Clean URL
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete(IMPERSONATION_TOKEN_PARAM);
                window.history.replaceState({}, document.title, newUrl.toString());
            }
            // Legacy: strip ?token= from URL
            if (urlParams.has('token')) {
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('token');
                window.history.replaceState({}, document.title, newUrl.toString());
            }
            // -- Cookie bootstrap ---------------------------------------------------
            const isDemoOrMissing = !currentToken || (skipDemoTokens && currentToken.startsWith('demo-token-'));
            try {
                const authApiUrl = getServiceApiUrl('auth');
                const localPayload = !isDemoOrMissing && currentToken
                    ? decodeJwtPayload(currentToken)
                    : null;
                const needsBootstrap = isDemoOrMissing || !localPayload?.tenantId;
                // The impersonation branch above may already have swapped currentToken
                // for an impersonation token, whose sub is the impersonated user.
                const isImpersonationToken = isLiveImpersonation(currentToken);
                // Check if the local token is expired — an expired token is unusable
                // even if it decodes successfully, so we must re-bootstrap from the
                // session cookie.
                const isLocalTokenExpired = localPayload?.exp
                    ? localPayload.exp < Math.floor(Date.now() / 1000)
                    : false;
                // Always reconcile the stored token against the authoritative session
                // cookie — even when the local token is unexpired and already carries a
                // tenantId. Such a token can still be STALE: e.g. it was minted for a
                // previously-selected org and left behind in localStorage. This block
                // used to be gated behind `needsBootstrap || isLocalTokenExpired`, so a
                // valid-but-stale wrong-tenant token was never compared against the
                // cookie and kept being sent — surfacing downstream as a dead-end
                // "No workspaces available" after the user switched orgs in the auth
                // app. The `shouldRefresh` check below still decides whether we actually
                // exchange the cookie, so the common (already-matching) case costs only
                // a single `/auth/me` request. A missing/unreachable cookie session
                // leaves `meResp.ok` false and the stored token untouched.
                {
                    const meResp = await fetch(`${authApiUrl}/auth/me`, {
                        signal: authRequestSignal(),
                        credentials: 'include',
                    });
                    if (meResp.ok) {
                        const meData = await meResp.json();
                        // The mount-time read, and the only one the common path makes: a valid
                        // non-stale token means no exchange and no top-up, so this is where the
                        // deadline has to be learned or it never is. Missing it left an
                        // actively-working user paced six hours out, refused at thirty minutes,
                        // with no warning shown because the countdown had nothing to count to.
                        // Third time this seeding gap has surfaced — hence the shared funnel.
                        // Flag before deadline: see `reconcileWithCookie`.
                        if (typeof meData.data?.session?.idleEnforced === 'boolean') {
                            idleEnforcedRef.current = meData.data.session.idleEnforced;
                            setIdleEnforced(meData.data.session.idleEnforced);
                        }
                        const cookieIdleExpiresAt = meData.data?.session?.idleExpiresAt;
                        // Support both { success, data: { user } } (new) and { success, user } (legacy)
                        const meUser = meData.data?.user ?? meData.user;
                        const cookieTenantId = meData.success ? meUser?.tenantId : null;
                        const cookieSub = meData.success ? meUser?.id : null;
                        // Pre-existing hole, closed while adjacent: an impersonation token
                        // carries the impersonated user's tenantId while the cookie carries
                        // the admin's, so this comparison also read impersonation as a
                        // stale token and re-bootstrapped it away on mount.
                        const tenantMismatch = !isImpersonationToken &&
                            !!cookieTenantId && cookieTenantId !== localPayload?.tenantId;
                        // Two accounts in one tenant produce byte-identical tenantId and
                        // workspaceId claims and differ only in sub/role, so a tenant
                        // comparison cannot see a same-tenant user swap. Comparing sub is
                        // the only check that catches it.
                        const identityMismatch = !isImpersonationToken &&
                            !!cookieSub && !!localPayload?.sub && cookieSub !== localPayload.sub;
                        // Same user, same org, different session: signed out and back in
                        // somewhere else. The stored token's session is gone and `/auth/me`
                        // refuses it, so keeping it signs out a user whose cookie is live.
                        // Not a reason to drop scope state — the person and org are the same.
                        const cookieJti = meData.data?.session?.jti ?? null;
                        const sessionMismatch = !isImpersonationToken &&
                            !!cookieJti && !!localPayload?.jti && cookieJti !== localPayload.jti;
                        const shouldRefresh = needsBootstrap || isLocalTokenExpired || tenantMismatch || identityMismatch || sessionMismatch;
                        // The cookie's deadline is this tab's only when the cookie names this
                        // tab's session; otherwise it waits for the exchange below to succeed.
                        // Adopting it up front and then failing the exchange left the old token
                        // paced and warned against another session's clock.
                        if (!needsBootstrap && !tenantMismatch && !identityMismatch && !sessionMismatch) {
                            adoptIdleDeadline(cookieIdleExpiresAt);
                        }
                        if (shouldRefresh && meData.success && meUser) {
                            // Does the scope state in localStorage belong to a previous
                            // identity or org? Decided from the mismatches themselves, NOT
                            // from whichever log branch below happens to win: `expired` sits
                            // first in that chain, so a token that is *both* expired and
                            // another user's would re-bootstrap to the cookie's identity and
                            // leave them holding the previous user's workspace selection.
                            //
                            // Acted on only after the exchange succeeds — clearing up front
                            // and then failing strips the workspace selection while leaving
                            // the user on the old token, damage from a re-bootstrap that
                            // accomplished nothing. Same ordering as reconcileWithCookie.
                            // (`&& !needsBootstrap` on the tenant arm preserves the original
                            // condition: a token with no tenantId at all is a cold start, not
                            // an org switch.)
                            const scopeIsStale = identityMismatch || (tenantMismatch && !needsBootstrap);
                            if (isLocalTokenExpired) {
                                console.log(`[${appName}] Local token expired, re-bootstrapping from session cookie`, identityMismatch ? { cookieSub, localSub: localPayload?.sub } : undefined);
                            }
                            else if (identityMismatch) {
                                console.log(`[${appName}] Session cookie names a different user than the stored token, re-bootstrapping`, { cookieSub, localSub: localPayload?.sub });
                            }
                            else if (tenantMismatch && !needsBootstrap) {
                                console.log(`[${appName}] Cookie tenantId differs from localStorage, re-bootstrapping`, { cookie: cookieTenantId, local: localPayload?.tenantId });
                            }
                            else if (sessionMismatch) {
                                console.log(`[${appName}] Session cookie names a newer session than the stored token, re-bootstrapping`);
                            }
                            // Exchange session cookie for Bearer token
                            const csrfToken = getCsrfToken();
                            const refreshHeaders = {
                                'Content-Type': 'application/json',
                            };
                            if (csrfToken)
                                refreshHeaders['x-csrf-token'] = csrfToken;
                            const refreshResp = await fetch(`${authApiUrl}/auth/refresh`, {
                                signal: authRequestSignal(),
                                method: 'POST',
                                headers: refreshHeaders,
                                credentials: 'include',
                            });
                            if (refreshResp.ok) {
                                const refreshData = await refreshResp.json();
                                // Support both { success, data: { token } } (new) and { success, token } (legacy)
                                const refreshedToken = refreshData.data?.token ?? refreshData.token;
                                if (refreshData.success && refreshedToken) {
                                    console.log(`[${appName}] Bootstrapped token from session cookie`);
                                    // Stale scope goes only now that a replacement token exists.
                                    if (scopeIsStale)
                                        clearScopeKeys();
                                    configRef.current.setToken(refreshedToken);
                                    localStorage.setItem(`${appName}:isRealUser`, 'true');
                                    // Clear the inactivity-logout flag
                                    sessionStorage.removeItem(`${appName}:loggedOutInactivity`);
                                    currentToken = refreshedToken;
                                    const bootstrapDeadline = refreshData.data?.idleExpiresAt ?? null;
                                    if (bootstrapDeadline) {
                                        idleDeadlineRef.current = bootstrapDeadline;
                                        setIdleExpiresAt(bootstrapDeadline);
                                    }
                                    else {
                                        // The tab now holds the cookie's session, so its deadline applies.
                                        adoptIdleDeadline(cookieIdleExpiresAt);
                                    }
                                }
                            }
                        }
                    }
                }
            }
            catch (e) {
                console.warn(`[${appName}] Cookie bootstrap failed`, e);
            }
            if (cancelled)
                return;
            // -- Fetch user & schedule refresh --------------------------------------
            // The deadline the bootstrap exchange reported, if it ran. Without seeding it
            // the first tick is paced purely off token expiry — six hours on a long-lived
            // token — so an actively-working user would be refused for inactivity hours
            // before the tab thought to ask.
            const isDemo = currentToken?.startsWith('demo-token-');
            if (currentToken && !isDemo) {
                const isImpersonation = !!getImpersonationInfo(currentToken);
                if (!isImpersonation && tokenNeedsRefresh(currentToken)) {
                    const newToken = await refreshToken(currentToken);
                    if (newToken) {
                        currentToken = newToken;
                    }
                }
                if (!cancelled) {
                    await doFetchUser();
                    if (isImpersonation) {
                        scheduleImpersonationHandback(currentToken);
                    }
                    if (!isImpersonation) {
                        // Immediately if the deadline learned above is already past and the
                        // server enforces it — otherwise this call would replace the immediate
                        // timer `adoptIdleDeadline` armed with an ordinary one-minute wait, and
                        // the app would render for that minute on a session the server has
                        // already given up on.
                        scheduleTokenRefresh(currentToken, undefined, deadlineOverdue() ? 0 : undefined);
                    }
                }
            }
            if (!cancelled) {
                setIsLoading(false);
            }
        };
        initAuth();
        return () => {
            cancelled = true;
            if (impersonationTimerRef.current) {
                window.clearTimeout(impersonationTimerRef.current);
                impersonationTimerRef.current = null;
            }
            if (refreshTimerRef.current) {
                window.clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
            if (finishTimerRef.current) {
                window.clearTimeout(finishTimerRef.current);
                finishTimerRef.current = null;
            }
        };
        // We intentionally only run on mount — config changes should not re-trigger
        // bootstrap. The configRef pattern keeps callbacks fresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ---- Logout & impersonation exit ----------------------------------------
    const logout = useCallback(async () => {
        // Mark this as a manual sign-out so ProtectedRoute yields navigation to
        // onLogout's clean login URL instead of re-appending `?redirect=`.
        signOutNavigationInProgress = true;
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
        if (impersonationTimerRef.current) {
            window.clearTimeout(impersonationTimerRef.current);
            impersonationTimerRef.current = null;
        }
        // Invalidate the session cookie on the auth server so that bfcache or
        // browser-back cannot re-bootstrap a token from the still-valid cookie.
        try {
            const authApiUrl = getServiceApiUrl('auth');
            const token = configRef.current.getToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token)
                headers['Authorization'] = `Bearer ${token}`;
            const csrfToken = getCsrfToken();
            if (csrfToken)
                headers['x-csrf-token'] = csrfToken;
            await fetch(`${authApiUrl}/auth/logout`, {
                signal: authRequestSignal(),
                method: 'POST',
                headers,
                credentials: 'include',
            });
        }
        catch (e) {
            console.warn(`[${appName}] Auth service logout failed (best-effort):`, e);
        }
        localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
        configRef.current.clearAuth();
        setUser(null);
        setImpersonation(null);
        // The cookie that was just invalidated backed every app on this origin, so
        // leaving their tokens behind leaves credentials with no session behind them
        // (ID-7). `clearAuth` only knows this app's keys; this clears every app's
        // and tells the open tabs, which would otherwise notice only at a remount
        // that may never come for a backgrounded one.
        signOutOfSuite('manual');
        configRef.current.onLogout('manual');
    }, [appName, IMPERSONATION_TOKEN_KEY]);
    const exitImpersonation = useCallback(async () => {
        const impersonationToken = localStorage.getItem(IMPERSONATION_TOKEN_KEY);
        if (impersonationToken) {
            try {
                const authServiceUrl = getServiceApiUrl('auth').replace(/\/api$/, '');
                await fetch(`${authServiceUrl}/api/admin/impersonation/end`, {
                    method: 'POST',
                    signal: authRequestSignal(),
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token: impersonationToken, exit_reason: 'manual_exit' }),
                });
            }
            catch (err) {
                console.warn(`[${appName}] Failed to log impersonation end:`, err);
            }
        }
        localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
        setImpersonation(null);
        configRef.current.clearAuth();
        setUser(null);
        window.close();
        window.location.href = '/';
    }, [appName, IMPERSONATION_TOKEN_KEY]);
    // ---- Context value -------------------------------------------------------
    const value = useMemo(() => ({
        appName,
        getToken,
        user,
        isAuthenticated: !!user,
        isLoading,
        refreshUser,
        logout,
        impersonation,
        exitImpersonation,
        idleExpiresAt,
        idleEnforced,
        reportActivity,
    }), [appName, getToken, user, isLoading, refreshUser, logout, impersonation, exitImpersonation, idleExpiresAt, idleEnforced, reportActivity]);
    return (_jsx(AppAuthContext.Provider, { value: value, children: children }));
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useAppAuth() {
    const context = useContext(AppAuthContext);
    if (!context) {
        throw new Error('useAppAuth must be used within an AppAuthProvider');
    }
    return context;
}
/**
 * The same context, or null outside a provider.
 *
 * For components that are useful with the session and harmless without it — the
 * expiry warning is mounted from each app's root, and whether that sits inside or
 * outside `AppAuthProvider` varies by app. Throwing there would take the whole
 * app down to avoid showing a toast.
 */
export function useOptionalAppAuth() {
    return useContext(AppAuthContext) ?? null;
}
